import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { DASHBOARD_PATH } from "@/constants/constant";
import {
  useClaimCampaignsApiCallMutation,
  useGetCampaignsDashboardApiCallQuery,
  useRedeemCodeMutation,
} from "@/services/api/campaigns/campaigns.api";
import { socket } from "@/_lib/socket";
import {
  Coins,
  Users,
  Clock,
  Twitter,
  Send,
  ExternalLink,
  ArrowRight,
  CheckCircle,
  Star,
  Loader2,
} from "lucide-react";
import { href, Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Campaign } from "@/services/api/campaigns/campaigns.type";
import { getTimeRemaining } from "@/utils/getTimeRemaining";
import { Badge } from "@/components/ui/badge";
import {
  useGetProfileApiCallQuery,
  useGetTokenPriceConversionApiCallQuery,
} from "@/services/api/auth/auth.api";
import { toast } from "sonner";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { ApiError } from "@/services/api/auth/auth.type";
import { Input } from "@/components/ui/input";
import Loader from "@/components/Loader";

export default function PrivateCampaign() {
  const campaigns = [
    {
      id: 1,
      name: "BINGOLD Genesis Launch",
      description: "Join the official launch campaign for BINGOLD tokens",
      totalPool: "1,000,000",
      reward: "500",
      participants: "12,847",
      progress: 75,
      timeLeft: "5 days",
      tasks: [
        { platform: "twitter", completed: true },
        { platform: "telegram", completed: true },
        { platform: "discord", completed: false },
      ],
      status: "active",
      featured: true,
      href: DASHBOARD_PATH.INDIVIDUAL_CAMPAIGN,
    },
    {
      id: 2,
      name: "Community Builder Campaign",
      description: "Earn rewards by building our community across platforms",
      totalPool: "500,000",
      reward: "250",
      participants: "8,932",
      progress: 100,
      timeLeft: "12 days",
      tasks: [
        { platform: "twitter", completed: true },
        { platform: "telegram", completed: true },
      ],
      status: "claimable",
      featured: false,
      href: DASHBOARD_PATH.INDIVIDUAL_CAMPAIGN,
    },
    {
      id: 3,
      name: "Early Adopter Rewards",
      description: "Special rewards for early BINGOLD adopters",
      totalPool: "750,000",
      reward: "400",
      participants: "15,234",
      progress: 67,
      timeLeft: "3 days",
      tasks: [
        { platform: "twitter", completed: true },
        { platform: "telegram", completed: false },
        { platform: "discord", completed: true },
      ],
      status: "active",
      featured: false,
      href: DASHBOARD_PATH.INDIVIDUAL_CAMPAIGN,
    },
    {
      id: 4,
      name: "Referral Program",
      description: "Invite friends and earn bonus BINGOLD tokens",
      totalPool: "300,000",
      reward: "150",
      participants: "6,721",
      progress: 0,
      timeLeft: "30 days",
      tasks: [
        { platform: "twitter", completed: false },
        { platform: "telegram", completed: false },
      ],
      status: "new",
      featured: false,
      href: DASHBOARD_PATH.INDIVIDUAL_CAMPAIGN,
    },
    {
      id: 5,
      name: "Content Creator Program",
      description: "Create content and earn BINGOLD rewards",
      totalPool: "400,000",
      reward: "200",
      participants: "9,156",
      progress: 0,
      timeLeft: "25 days",
      tasks: [
        { platform: "twitter", completed: false },
        { platform: "telegram", completed: false },
        { platform: "discord", completed: false },
      ],
      status: "new",
      featured: false,
      href: DASHBOARD_PATH.INDIVIDUAL_CAMPAIGN,
    },
    {
      id: 6,
      name: "Liquidity Provider Bonus",
      description: "Extra rewards for providing liquidity",
      totalPool: "600,000",
      reward: "350",
      participants: "4,892",
      progress: 0,
      timeLeft: "45 days",
      tasks: [
        { platform: "twitter", completed: false },
        { platform: "telegram", completed: false },
      ],
      status: "coming-soon",
      featured: false,
      href: DASHBOARD_PATH.INDIVIDUAL_CAMPAIGN,
    },
  ];

  const { data: user } = useGetProfileApiCallQuery();
  const [campaignsData, setCampaignsData] = useState<Campaign[] | null>(null);
  const [isWsLoading, setIsWsLoading] = useState(false);
  const [claimRewardsCall] = useClaimCampaignsApiCallMutation();
  const [redeemCode, setRedeemCode] = useState<string>(() => {
    return localStorage.getItem("redeemCode") || "";
  });
  const [redeemCodeCall, { isLoading: isRedeeming }] = useRedeemCodeMutation();
  const [shouldConnectSocket, setShouldConnectSocket] = useState(() => {
    return localStorage.getItem("shouldConnectSocket") === "true";
  });

  // const { data: campaignsData } = useGetCampaignsDashboardApiCallQuery();
  const wsRef = useRef<WebSocket | null>(null);

  const claimRewards = async (id: string) => {
    try {
      const response = await claimRewardsCall({
        campaignId: id,
      }).unwrap();

      if (response?.responseCode === 200) {
        toast.success(response?.responseMessage);
      } else {
        toast.error(response?.responseMessage);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Error claiming rewards");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "claimable":
        return "bg-green-500";
      case "Active":
        return "bg-blue-500";
      case "Completed":
        return "bg-blue-500";
      case "new":
        return "bg-yellow-500";
      case "Upcoming":
        return "bg-yellow-500";
      case "coming-soon":
        return "bg-gray-500";
      default:
        return "bg-gray-500";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "claimable":
        return "Ready to Claim";
      case "Active":
        return "Active";
      case "Completed":
        return "Completed";
      case "new":
        return "New";
      case "coming-soon":
        return "Coming Soon";
      case "Upcoming":
        return "Upcoming";
      default:
        return "--";
    }
  };

  const getButtonText = (status: string, progress: number) => {
    if (status === "claimable") return "Claim Rewards";
    if (status === "coming-soon") return "Notify Me";
    if (progress > 0) return "Continue Tasks";
    return "Start Campaign";
  };

  const getButtonStyle = (status: string) => {
    if (status === "claimable") {
      return "bg-gradient-to-r from-green-400 to-green-600 text-black hover:from-green-500 hover:to-green-700";
    }
    if (status === "coming-soon") {
      return "bg-gray-600 text-white hover:bg-gray-700 cursor-not-allowed";
    }
    return "bg-gradient-to-r from-yellow-400 to-yellow-600 text-black hover:from-yellow-500 hover:to-yellow-700";
  };

  const handleRedeemCode = async () => {
    if (!redeemCode.trim()) {
      toast.error("Please enter a code");
      return;
    }

    try {
      const res = await redeemCodeCall({ code: redeemCode.trim() }).unwrap();
      toast.success(res.responseMessage || "Code redeemed successfully!");
      setShouldConnectSocket(true);
      localStorage.setItem("shouldConnectSocket", "true");

      localStorage.setItem("redeemCode", redeemCode);
    } catch (err: any) {
      toast.error(err?.data?.responseMessage || "Invalid or expired code");
    }
  };

  useEffect(() => {
    console.log("callllllllllllllllllllll", shouldConnectSocket, redeemCode);
    if (!shouldConnectSocket) return;

    setIsWsLoading(true);
    wsRef.current = new WebSocket("wss://node-airdrop.bingold.to");
    // wsRef.current = new WebSocket("ws://localhost:3000"); // Your backend WS URL

    // wsRef.current = new WebSocket("wss://node-airdrop.bingold.to");

    const ws = wsRef.current;

    ws.onopen = () => {
      console.log("Connected to server");

      ws.send(
        JSON.stringify({
          options: "getLiveCampaigns",
          userId: user?.result?._id,
          campaignType: "Private",
          code: redeemCode.trim(),
        }),
      );
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log(data, "data.............................");
        setCampaignsData(data?.data?.docs);
      } catch (err) {
        console.error("WS message parse error:", err);
      } finally {
        setIsWsLoading(false);
      }
    };

    ws.onerror = (err) => {
      console.error("WebSocket error:", err);
      setIsWsLoading(false);
    };

    ws.onclose = () => {
      setIsWsLoading(false);
    };

    return () => {
      ws.close();
    };
  }, [redeemCode, user, shouldConnectSocket]);

  const { data: tokenPriceData } = useGetTokenPriceConversionApiCallQuery();

  const getUSDTBalance = (walletBalance: number) => {
    const usdtPrice =
      tokenPriceData?.result?.data?.find((item: any) => item.coin === "usdt")
        ?.tokenPrice || 0;
    const balance = walletBalance * usdtPrice || 0;
    return balance;
  };
  if (isWsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader size="lg" text="Loading private campaigns..." />
      </div>
    );
  }
  return (
    <section id="airdrop" className="py-20 text-white">
      <div className="container mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Active BINGOLD{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">
              Campaigns
            </span>
          </h2>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
            Complete social media tasks and earn your share of BINGOLD tokens.
            Each campaign offers unique rewards and requirements.
          </p>
        </div>
        {/* Floating Redeem Box - Top Right Corner */}
        <div className="fixed top-25 left-4 right-4 sm:top-25 sm:left-auto sm:right-8 z-50 animate-in fade-in slide-in-from-top-4 duration-500 max-w-lg mx-auto sm:mx-0">
          {" "}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-gray-900/90 backdrop-blur-md border border-yellow-500/30 rounded-xl p-4 shadow-2xl shadow-black/50">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full">
              <Input
                placeholder="Enter redeem code..."
                value={redeemCode}
                onChange={(e) => setRedeemCode(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleRedeemCode()}
                className="bg-gray-800/70 border-yellow-600/40 text-white placeholder:text-gray-400 focus:ring-2 focus:ring-yellow-400/30 h-11 w-full sm:flex-1 sm:min-w-0"
              />
              <Button
                onClick={handleRedeemCode}
                disabled={isRedeeming || !redeemCode.trim()}
                className="h-11 px-6 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-black font-bold shadow-lg disabled:opacity-70 disabled:cursor-not-allowed whitespace-nowrap flex-shrink-0"
              >
                {isRedeeming ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Redeeming...
                  </>
                ) : (
                  "Redeem Now"
                )}
              </Button>
            </div>
          </div>
          {/* Optional: Small glow effect */}
          <div className="absolute inset-0 -z-10 rounded-xl bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 blur-2xl" />
        </div>

        {/* Token Info */}
        <div className="bg-gradient-to-r from-yellow-400/10 to-yellow-600/10 border border-yellow-500/20 rounded-2xl p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-2xl font-bold text-yellow-400 mb-2">
                BINGOLD
              </div>
              <div className="text-gray-400">Token Symbol</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-400 mb-2">BSC</div>
              <div className="text-gray-400">Blockchain</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-400 mb-2">
                2,500,000
              </div>
              <div className="text-gray-400">Total Supply</div>
            </div>
          </div>
        </div>

        {/* Campaign Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {campaignsData &&
            campaignsData.length > 0 &&
            campaignsData.map((campaign) => {
              const { days, hours, minutes } = getTimeRemaining(
                campaign.startDate,
                campaign.endDate,
              );
              return (
                <Card
                  key={campaign._id}
                  className={`bg-gray-900/50 border-yellow-500/20 hover:border-yellow-500/40 transition-all duration-300 group ${
                    true ? "ring-2 ring-yellow-500/30" : ""
                  }`}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-white text-lg font-semibold">
                            {campaign.name}
                          </h3>
                          {/* {campaign.featured && (
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      )} */}
                        </div>
                        <p
                          className="text-gray-400 text-sm leading-relaxed"
                          style={{
                            wordBreak: "break-all",
                          }}
                        >
                          {campaign.description}
                        </p>
                      </div>
                    </div>

                    <Badge
                      className={`${getStatusColor(
                        campaign.campaignStatus,
                      )} text-white w-fit`}
                    >
                      {getStatusText(campaign.campaignStatus)}
                    </Badge>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* Reward Info */}
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className="text-yellow-400 font-semibold">
                          {campaign.airdropAmount}
                        </div>
                        <div className="text-gray-400 text-xs">
                          BINGOLD Reward
                        </div>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <div className="text-white font-semibold">
                          {campaign.totalJoined}
                        </div>
                        <div className="text-gray-400 text-xs">
                          Participants
                        </div>
                      </div>
                    </div>

                    {/* Progress (only for active campaigns) */}
                    {campaign.status !== "coming-soon" && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Your Progress</span>
                          <span className="text-white">
                            {Number(
                              (campaign.allVerifiedTasks / campaign.allTasks) *
                                100,
                            )}
                            % Complete
                          </span>
                        </div>
                        <Progress
                          value={
                            Math.floor(
                              campaign.allVerifiedTasks / campaign.allTasks,
                            ) * 100
                          }
                          className="h-2 bg-gray-800"
                        >
                          <div
                            className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full transition-all"
                            style={{
                              width: `${Math.floor(
                                (campaign.allVerifiedTasks /
                                  campaign.allTasks) *
                                  100,
                              )}%`,
                            }}
                          />
                        </Progress>
                      </div>
                    )}

                    {/* Social Platforms */}
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Worth $ </span>
                      <span>
                        {" "}
                        $
                        {Number(getUSDTBalance(campaign.airdropAmount)).toFixed(
                          2,
                        )}{" "}
                      </span>
                      {/* <div className="flex space-x-2">
                      {campaign.tasks.map((task, index) => {
                        const Icon =
                          task.platform === "twitter"
                            ? Twitter
                            : task.platform === "telegram"
                            ? Send
                            : Users;
                        return (
                          <div
                            key={index}
                            className={`w-6 h-6 rounded-full flex items-center justify-center ${
                              task.completed ? "bg-green-500" : "bg-gray-600"
                            }`}
                          >
                            <Icon className="w-3 h-3 text-white" />
                          </div>
                        );
                      })}
                    </div> */}
                    </div>

                    {/* Time Left */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 text-gray-400">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm">
                          {days}d {hours}h {minutes}m left
                        </span>
                      </div>
                      <div className="text-gray-400 text-sm">
                        Pool: {campaign.totalPool} BINGOLD
                      </div>
                    </div>

                    {/* Action Button */}

                    {campaign.campaignStatus === "Completed" &&
                    !campaign?.isClaimable &&
                    !campaign?.isClaimed ? (
                      <div className="relative group">
                        <Button
                          className="bg-red-500/80 w-full cursor-not-allowed opacity-70 flex items-center justify-center gap-2"
                          disabled
                        >
                          <Clock className="w-4 h-4" />
                          Expired
                        </Button>

                        {/* Tooltip with user-friendly message */}
                        <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                          <div className="bg-gray-800 border border-red-500/50 rounded-lg p-2 shadow-lg min-w-[200px]">
                            <div className="flex items-center gap-2 text-sm text-red-300">
                              <Clock className="w-3 h-3 flex-shrink-0" />
                              <span>
                                This campaign has ended. <br />
                                <span className="text-red-200 font-medium">
                                  Check other active campaigns!
                                </span>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        {" "}
                        {campaign?.claimType === "Completion" &&
                        campaign?.isClaimable &&
                        !campaign?.isClaimed ? (
                          <Button className="w-full cursor-pointer">
                            Completed
                          </Button>
                        ) : campaign?.isClaimed ? (
                          <Button className="bg-green-500 w-full cursor-pointer">
                            Claimed
                          </Button>
                        ) : campaign?.isClaimable ? (
                          <Button
                            className="bg-green-500 w-full cursor-pointer"
                            onClick={() => claimRewards(campaign._id)}
                          >
                            Claim Rewards
                          </Button>
                        ) : !campaign?.isClaimable && campaign?.isJoined ? (
                          <Link
                            to={`${DASHBOARD_PATH?.INDIVIDUAL_CAMPAIGN}/${campaign._id}`}
                          >
                            <Button className="bg-gray-500 w-full cursor-pointer">
                              Continue Task
                            </Button>
                          </Link>
                        ) : (
                          <Link
                            to={`${DASHBOARD_PATH?.INDIVIDUAL_CAMPAIGN}/${campaign._id}`}
                            className="cursor-pointer"
                          >
                            <Button className="w-full cursor-pointer">
                              Start Campaign
                            </Button>
                          </Link>
                        )}
                      </>
                    )}

                    {/* {campaign?.claimType !== "Instant" &&
                    campaign?.isClaimed ? (
                      <Button
                        className="bg-green-500 w-full cursor-pointer"
                        // onClick={() => claimRewards(campaign._id)}
                      >
                        Claimed
                      </Button>
                    ) : campaign?.isClaimable ? (
                      <Button
                        className="bg-green-500 w-full cursor-pointer"
                        onClick={() => claimRewards(campaign._id)}
                      >
                        Claim Rewards
                      </Button>
                    ) : !campaign?.isClaimable && campaign?.isJoined ? (
                      <Link
                        to={`${DASHBOARD_PATH?.INDIVIDUAL_CAMPAIGN}/${campaign._id}`}
                      >
                        <Button className="bg-gray-500 w-full cursor-pointer">
                          Continue Task
                        </Button>
                      </Link>
                    ) : (
                      <Link
                        to={`${DASHBOARD_PATH?.INDIVIDUAL_CAMPAIGN}/${campaign._id}`}
                        className="cursor-pointer"
                      >
                        <Button className="w-full cursor-pointer">
                          Start Campaign
                        </Button>
                      </Link>
                    )} */}
                    {/* <Link to={campaign.href}>
                    <Button
                      className={`w-full cursor-pointer ${getButtonStyle(
                        campaign.status
                      )}`}
                      disabled={campaign.status === "coming-soon"}
                    >
                      {getButtonText(campaign.status, campaign.progress)}
                      {campaign.status !== "coming-soon" && (
                        <ArrowRight className="w-4 h-4 ml-2" />
                      )}
                    </Button>
                  </Link> */}
                  </CardContent>
                </Card>
              );
            })}
        </div>

        {(!campaignsData || campaignsData.length === 0) && (
          <div className="text-center text-gray-400">No campaigns found.</div>
        )}

        {/* Call to Action */}
        {/* <div className="text-center">
          <div className="bg-gradient-to-r from-yellow-400/10 to-yellow-600/10 border border-yellow-500/20 rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to Start Earning?
            </h3>
            <p className="text-gray-300 mb-6">
              Connect your wallet to participate in BINGOLD campaigns and start
              earning tokens today.
            </p>
            <Button className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black hover:from-yellow-500 hover:to-yellow-700 px-8 py-3">
              Connect Wallet & Start
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div> */}
      </div>
    </section>
  );
}
